import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Attr } from '@mode-switch/model';
import { EncContinuousFilterField, EncFilterField, Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { LoaderComponent } from '@mode/shared/ui';
import { CompiledField } from '@mode/shared/util-switch';
import { BehaviorSubject, EMPTY, Observable, Subject, debounce, first, takeUntil, timer } from 'rxjs';
import * as util from '../../filter-modal-helpers';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  selector: 'mode-filter-range-numeric',
  templateUrl: './filter-range-numeric.component.html',
  styleUrls: ['./filter-range-numeric.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterRangeNumericComponent implements OnInit, OnChanges, OnDestroy {
  @Input() field!: CompiledField;
  @Input() filter!: EncContinuousFilterField;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() encoding!: Encoding;
  @Input() queryRunToken!: string;

  @Output() filterChange = new EventEmitter<EncFilterField>();
  @Output() isInvalid = new EventEmitter<boolean>();

  private domainAlias: string | undefined;

  dataMin: number | null = null;
  dataMax: number | null = null;
  min: number | null = null;
  max: number | null = null;

  private isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  private destroyed$ = new Subject<void>();

  isLoading$: Observable<boolean>;
  showLoader$: Observable<boolean>;

  constructor(private chartFacade: ChartFacade) {
    this.isLoading$ = this.isLoadingSubject$.asObservable().pipe(takeUntil(this.destroyed$));
    this.showLoader$ = this.isLoadingSubject$.asObservable().pipe(
      takeUntil(this.destroyed$),
      debounce((showLoader) => (showLoader ? timer(350) : EMPTY))
    );
  }

  ngOnInit(): void {
    const refId = 'filter-numeric-range';
    this.domainAlias = util.idFromFormula(this.field.formula);

    // Handle any query errors
    this.chartFacade
      .createErrorFromDomainQuery(refId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.onLoadError(error));

    this.chartFacade.fetchFilterDomainValues(
      this.queryRunToken,
      this.view,
      this.encoding,
      this.field,
      refId,
      this.domainAlias,
      false
    );

    this.chartFacade
      .createSelectDomainValues(refId, this.domainAlias ?? '')
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result != null && result.type === Attr.Type.Continuous) {
          this.dataMin = result.min;
          this.dataMax = result.max;
        }
        this.isLoadingSubject$.next(false);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter !== undefined) {
      this.min = this.filter.range.min;
      this.max = this.filter.range.max;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onMinChange(min: number) {
    this.min = min;
    this.onInputChange();
  }

  onMaxChange(max: number) {
    this.max = max;
    this.onInputChange();
  }

  onInputChange() {
    if (this.validate()) {
      this.filterChange.emit({
        ...this.filter,
        range: {
          min: this.min as number,
          max: this.max as number,
        },
      });
    }
  }

  onFocusInput($event: FocusEvent) {
    // Select current contents
    ($event.target as HTMLInputElement)?.select();
  }

  onBlurInput() {
    this.validate();
  }

  onLoadError(error: FlamingoResponse.FlamingoErrorResponse | undefined) {
    if (error !== undefined) {
      // Clear the loading indicator, but don't set an error state.
      // The inputs are still usable, we just don't have the data min/max.
      // If this is an existing filter, those defaults may not even matter.
      this.isLoadingSubject$.next(false);
      console.warn(`Unable to load filter data range. Error: ${JSON.stringify(error)}`);
    }
  }

  validate() {
    // Validate that the min < max if set
    if (this.min !== null && this.max !== null && this.min > this.max) {
      this.isInvalid.emit(true);
      return false;
    }
    this.isInvalid.emit(false);
    return true;
  }
}
